// AppLayout.tsx
import { ReactNode } from "react";
import { SidebarTrigger, SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { SEOHead } from "@/components/SEOHead";
import { Button } from "@/components/ui/button";
import {
  Zap,
  Bell,
  ChevronRight,
  Home,
  Search,
  Plus,
  Settings,
  HelpCircle,
  Activity,
  Star,
  Code,
  Calendar,
  Users,
  BarChart3,
  Eye,
  Sparkles,
  Palette,
} from "lucide-react";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { useLocation, Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { UserProfileDropdown } from "@/components/auth/UserProfileDropdown";

interface AppLayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  useCustomHeader?: boolean;
  customHeader?: ReactNode;
  headerTitle?: string;
  headerSubtitle?: string;
  headerIcon?: React.ComponentType<{ className?: string }>;
  headerActions?: React.ReactNode;
  showBreadcrumb?: boolean;
  headerClass?: string;
  hideHeader?: boolean;
}

// Page configurations for dynamic header
const pageConfigs: Record<string, {
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  gradient: string;
}> = {
  '/': {
    title: 'Event Generator',
    subtitle: 'Create amazing events with AI',
    icon: Sparkles,
    gradient: 'from-theme-primary to-theme-accent'
  },
  '/dashboard': {
    title: 'Dashboard',
    subtitle: 'Overview of your activities',
    icon: BarChart3,
    gradient: 'from-blue-500 to-cyan-500'
  },
  '/events': {
    title: 'Events',
    subtitle: 'Manage your events',
    icon: Calendar,
    gradient: 'from-green-500 to-emerald-500'
  },
  '/analytics': {
    title: 'Analytics',
    subtitle: 'Insights and performance metrics',
    icon: BarChart3,
    gradient: 'from-orange-500 to-red-500'
  },
  '/templates': {
    title: 'Templates',
    subtitle: 'Pre-built event templates',
    icon: Eye,
    gradient: 'from-teal-500 to-green-500'
  },
  '/settings': {
    title: 'Settings',
    subtitle: 'Configure your preferences',
    icon: Settings,
    gradient: 'from-gray-500 to-slate-500'
  },
  '/code-editor': {
    title: 'Code Editor',
    subtitle: 'Edit and customize your code',
    icon: Code,
    gradient: 'from-indigo-500 to-purple-500'
  }
};

// Generate breadcrumbs from current path
const generateBreadcrumbs = (pathname: string) => {
  const segments = pathname.split('/').filter(Boolean);
  const breadcrumbs = [];

  breadcrumbs.push({
    label: 'Home',
    href: '/',
    icon: Home
  });

  let currentPath = '';
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === segments.length - 1;
    const config = pageConfigs[currentPath];
    const label = config?.title || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');

    breadcrumbs.push({
      label,
      href: currentPath,
      icon: config?.icon,
      isLast
    });
  });

  return breadcrumbs;
};

// Default Header Component
const DefaultHeader = ({
  pageConfig,
  breadcrumbs,
  showBreadcrumb,
  headerActions,
  headerClass
}: {
  pageConfig: any;
  breadcrumbs: any[];
  showBreadcrumb?: boolean;
  headerActions?: ReactNode;
  headerClass?: string;
}) => (
  <header className={cn(
    "sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
    headerClass
  )}>
    <div className="flex h-16 items-center gap-4 px-6">
      <SidebarTrigger className="-ml-1" />

      {/* Breadcrumb */}
      {showBreadcrumb && breadcrumbs.length > 1 && (
        <Breadcrumb className="hidden md:flex">
          <BreadcrumbList>
            {breadcrumbs.map((crumb, index) => (
              <div key={crumb.href} className="flex items-center gap-2">
                {index > 0 && <BreadcrumbSeparator />}
                <BreadcrumbItem>
                  {crumb.isLast ? (
                    <BreadcrumbPage className="flex items-center gap-2">
                      {crumb.icon && <crumb.icon className="h-4 w-4" />}
                      {crumb.label}
                    </BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={crumb.href} className="flex items-center gap-2">
                        {crumb.icon && <crumb.icon className="h-4 w-4" />}
                        {crumb.label}
                      </Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
              </div>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      )}

      <div className="flex-1" />

      {/* Header Actions */}
      {headerActions}

      {/* Right side actions */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="icon" className="h-9 w-9">
          <Bell className="h-4 w-4" />
        </Button>
        <ThemeSwitcher />
        <UserProfileDropdown />
      </div>
    </div>
  </header>
);

export const AppLayout = ({
  children,
  title,
  description,
  useCustomHeader = false,
  customHeader,
  headerTitle,
  headerSubtitle,
  headerIcon,
  headerActions,
  showBreadcrumb = true,
  headerClass,
  hideHeader = false
}: AppLayoutProps) => {
  const location = useLocation();
  const currentPath = location.pathname;

  // Get page configuration
  const pageConfig = pageConfigs[currentPath] || {
    title: headerTitle || 'Swaraah',
    subtitle: headerSubtitle || 'AI Platform',
    icon: headerIcon || Zap,
    gradient: 'from-theme-primary to-theme-accent'
  };

  const breadcrumbs = generateBreadcrumbs(currentPath);

  return (
    <>
      <SEOHead title={title || pageConfig.title} description={description || pageConfig.subtitle} />
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          {!hideHeader && (
            useCustomHeader && customHeader ? customHeader : (
              <DefaultHeader
                pageConfig={pageConfig}
                breadcrumbs={breadcrumbs}
                showBreadcrumb={showBreadcrumb}
                headerActions={headerActions}
                headerClass={headerClass}
              />
            )
          )}

          {/* Main Content */}
          <main className="flex-1 flex flex-col overflow-hidden">
            {children}
          </main>
        </SidebarInset>
      </SidebarProvider>
    </>
  );
};
